import type { ActorView, FileChangeEventView } from '@/lib/api/generated/model';

/**
 * One file's recent changes, as one row (spec 127 FR-005).
 *
 * Grouped by file, not by consecutive actor: in practice two tasks writing
 * one shared file alternate, and grouping runs of one actor collapsed nothing
 * — the Overview showed `README.md` five times.
 */
export interface FileChangeSummary {
  path: string;
  /** The newest event for the file. */
  latest: FileChangeEventView;
  count: number;
  /** Who changed it, most recent first, each once. */
  actors: ActorView[];
}

function actorKey(actor: ActorView): string {
  return JSON.stringify([
    actor.kind,
    actor.user_id ?? null,
    actor.task_id ?? null,
  ]);
}

/** `events` newest first, as the feed returns them. */
export function summariseChanges(
  events: readonly FileChangeEventView[],
  limit: number,
): FileChangeSummary[] {
  const byPath = new Map<string, FileChangeSummary>();
  const seenActors = new Map<string, Set<string>>();
  for (const event of events) {
    let entry = byPath.get(event.path);
    if (!entry) {
      entry = { path: event.path, latest: event, count: 0, actors: [] };
      byPath.set(event.path, entry);
      seenActors.set(event.path, new Set());
    }
    entry.count += 1;
    const key = actorKey(event.actor);
    const seen = seenActors.get(event.path);
    if (seen && !seen.has(key)) {
      seen.add(key);
      entry.actors.push(event.actor);
    }
  }
  return [...byPath.values()].slice(0, limit);
}

/**
 * A file's latest change as the start of a sentence, actor first: "Payments
 * produced", "Alice changed" (spec 153 FR-015). The caller adds the file.
 *
 * An agent's work is what it produced, whether the file was new or not: the
 * reader's question is what the agents made. A person added or changed it.
 * The verb follows the newest change, made by the first actor named.
 *
 * Names come only from the change: a task's name, then its id, as the history
 * pane falls back; an unattributed change is "An agent", which is all anyone
 * knows of it.
 */
export function changeSentence(file: FileChangeSummary): {
  who: string;
  verb: string;
} {
  // A named actor leads: "Payments and another agent", never "An agent and
  // Payments". The unattributed change is the one nobody can say more about.
  const actors = [
    ...file.actors.filter((actor) => actor.kind !== 'unattributed'),
    ...file.actors.filter((actor) => actor.kind === 'unattributed'),
  ];
  const names = actors.map((actor, index) =>
    index > 0 && actor.kind === 'unattributed'
      ? 'another agent'
      : nameOf(actor),
  );
  const [first = 'Someone', second] = names;
  const who =
    names.length <= 1
      ? first
      : names.length === 2
        ? `${first} and ${String(second)}`
        : `${first} and ${String(names.length - 1)} others`;
  return { who, verb: verbOf(file.latest) };
}

function nameOf(actor: ActorView): string {
  if (actor.kind === 'human')
    return actor.user_name ?? actor.user_id ?? 'Someone';
  if (actor.kind === 'unattributed') return 'An agent';
  return actor.task_name ?? actor.task_id ?? 'An agent';
}

function verbOf(event: FileChangeEventView): string {
  switch (event.event_type) {
    case 'deleted':
      return 'removed';
    case 'moved':
      return 'moved';
    case 'created':
      return event.actor.kind === 'human' ? 'added' : 'produced';
    case 'modified':
      return event.actor.kind === 'human' ? 'changed' : 'produced';
  }
}
